import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  viewChild,
} from '@angular/core';
import { DeclarativeMapLayerComponent } from '@cue/map/elements';
import { CalendarAvailability, getCalendarWorkingHoursStatusAt } from '@cue/calendars';
import { NAVIGATION_SETTINGS } from '@cue/navigation-settings';
import { WorkingHourInfo } from '@cue/ui';
import { getTimeZoneFromOfficeString } from '@cue/data';

@Component({
  selector: 'cue-map-resource',
  imports: [DeclarativeMapLayerComponent],
  templateUrl: './map-resource.component.html',
  styleUrl: './map-resource.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MapResourceComponent {
  fitMap = input(false);
  navigationSettings = inject(NAVIGATION_SETTINGS);

  statusColorMap = {
    [CalendarAvailability.free]: this.navigationSettings.design.background.reservableResource.free,
    [CalendarAvailability.tentative]: this.navigationSettings.design.background.reservableResource.tentative,
    [CalendarAvailability.limited]: this.navigationSettings.design.background.reservableResource.limited,
    [CalendarAvailability.reserved]: this.navigationSettings.design.background.reservableResource.reserved,
    [CalendarAvailability.closed]: this.navigationSettings.design.background.reservableResource.closed,
    [CalendarAvailability.freeOutsideWorkingHours]:
      this.navigationSettings.design.background.reservableResource.freeClosed,
  };

  private removeAlphaFromRgba(color: string): string {
    const rgbaPattern = /^rgba\((\d{1,3}),\s*(\d{1,3}),\s*(\d{1,3}),\s*[\d.]+\)$/;
    const match = color.match(rgbaPattern);

    // je to RGBA
    if (match) {
      const r = match[1];
      const g = match[2];
      const b = match[3];
      return `rgba(${r}, ${g}, ${b})`;
    } else {
      //neni RGBA vrat primo barvu
      return color;
    }
  }
  click = output();

  relativeNow = input<Date>();

  now = computed(() => {
    return this.relativeNow() || new Date();
  });

  workingHourInfo = input<WorkingHourInfo>();

  private resourceTimeZone = computed(() => {
    const timeZoneInfo = this.workingHourInfo().workingHours.timeZoneInfo;
    const normalizedTimeZone = timeZoneInfo === 'Custom time zone' ? 'UTC' : timeZoneInfo;

    return getTimeZoneFromOfficeString(normalizedTimeZone) ?? normalizedTimeZone;
  });

  tillStart = computed(() => {
    const status = getCalendarWorkingHoursStatusAt({
      workingHours: this.workingHourInfo().workingHours.workingHoursSetting,
      timeZone: this.resourceTimeZone(),
      at: this.now(),
    });

    if (status.isOpen) {
      return { hours: 0, minutes: 0 };
    }

    if (status.minutesUntilNextOpen === null) {
      return { hours: -1, minutes: -1 };
    }

    return {
      hours: Math.floor(status.minutesUntilNextOpen / 60),
      minutes: status.minutesUntilNextOpen % 60,
    };
  });

  isOpen = computed(() => {
    return this.tillStart().hours === 0 && this.tillStart().minutes === 0;
  });

  geoJson = input<any>();
  radius = input<number>(10);

  focused = input<boolean, boolean | 'true' | 'false'>(false, {
    transform: booleanAttribute,
  });

  reservable = input<boolean, boolean | 'true' | 'false'>(false, {
    transform: booleanAttribute,
  });

  availability = input<CalendarAvailability>();

  weight = computed(() => {
    return this.focused() ? 3 : 2;
  });

  fillOpacity = computed(() => {
    return this.focused() ? 0.85 : 0.4;
  });

  fillColor = computed(() => {
    if (this.availability() && this.reservable()) {
      const colorTemp = this.statusColorMap[this.availability()] ?? 'rgba(130, 11, 138, 0.5)';
      return this.removeAlphaFromRgba(colorTemp);
    }

    if (this.workingHourInfo()?.workingHours && this.workingHourInfo()?.workingHours?.timeZoneInfo) {
      return this.isOpen()
        ? this.navigationSettings.design.background.nonReservableResource.opened
        : this.navigationSettings.design.background.nonReservableResource.closed;
    } else {
      const data = this.workingHourInfo();
      return this.navigationSettings.design.background.nonReservableResource.withoutWorkingHours;
    }
  });

  color = computed(() => {
    return this.fillColor();
  });

  layerComp = viewChild(DeclarativeMapLayerComponent);
  focus() {
    this.layerComp()?.focus();
  }

  onLayerClick() {
    this.click.emit();
  }
}
